import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import { Cookie } from "lucide-react";
import { EASE } from "../ui/Reveal";
import { OPEN_EVENT, OPTIONAL_CATEGORIES as OPTIONAL, readConsent, saveConsent } from "../../lib/consent";
import { clearPreferences } from "../../lib/preferences";

const Toggle = ({ checked, disabled, onChange, label, desc, badge }) => (
  <label className={`flex items-start gap-3 rounded-xl border border-zinc-200 p-3.5 ${disabled ? "" : "cursor-pointer"}`}>
    <input
      type="checkbox"
      className="mt-1 h-4 w-4 accent-rose flex-shrink-0"
      checked={checked}
      disabled={disabled}
      onChange={(e) => onChange?.(e.target.checked)}
    />
    <span className="min-w-0">
      <span className="flex items-center gap-2 text-sm font-bold text-ink">
        {label}
        {badge && <span className="text-[10px] font-semibold text-zinc-500 border border-zinc-200 rounded-full px-2 py-0.5">{badge}</span>}
      </span>
      <span className="block text-xs text-zinc-500 leading-relaxed mt-0.5">{desc}</span>
    </span>
  </label>
);

const CookieConsent = () => {
  const { t, i18n } = useTranslation();
  const [visible, setVisible] = useState(() => !readConsent());
  const [expanded, setExpanded] = useState(false);
  const [choices, setChoices] = useState(() => {
    const saved = readConsent();
    return Object.fromEntries(OPTIONAL.map((k) => [k, !!saved?.[k]]));
  });

  useEffect(() => {
    const open = () => {
      const saved = readConsent();
      setChoices(Object.fromEntries(OPTIONAL.map((k) => [k, !!saved?.[k]])));
      setExpanded(true);
      setVisible(true);
    };
    window.addEventListener(OPEN_EVENT, open);
    return () => window.removeEventListener(OPEN_EVENT, open);
  }, []);

  if (!visible) return null;

  const finish = (next) => {
    saveConsent(next);
    if (!next.preferences) clearPreferences();
    setVisible(false);
    setExpanded(false);
  };

  const allowAll = () => finish(Object.fromEntries(OPTIONAL.map((k) => [k, true])));
  const deny = () => finish(Object.fromEntries(OPTIONAL.map((k) => [k, false])));
  const allowSelection = () => finish(choices);

  const secondary =
    "inline-flex items-center justify-center rounded-xl bg-zinc-100 px-4 py-2.5 text-sm font-semibold text-ink hover:bg-gray-200 transition-colors";

  return (
    <motion.div
      role="dialog"
      aria-modal="false"
      aria-labelledby="cookie-title"
      initial={{ opacity: 0, y: 24 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, ease: EASE }}
      dir={i18n.language === "ar" ? "rtl" : "ltr"}
      className="fixed inset-x-3 bottom-3 sm:inset-x-6 sm:bottom-6 z-[70] mx-auto max-w-5xl rounded-2xl border border-zinc-200 bg-white p-5 sm:p-6 shadow-2xl font-inter"
    >
      <div className="flex flex-col lg:flex-row lg:items-center gap-5">
        <div className="flex items-start gap-3 flex-1 min-w-0">
          <span className="w-10 h-10 rounded-xl bg-rose-soft text-rose flex items-center justify-center flex-shrink-0">
            <Cookie size={20} />
          </span>
          <div>
            <h2 id="cookie-title" className="text-base font-extrabold text-ink">{t("cookies.title")}</h2>
            <p className="text-sm text-zinc-500 leading-relaxed mt-1">{t("cookies.text")}</p>
          </div>
        </div>

        <div className="flex flex-wrap gap-2 lg:justify-end">
          <button type="button" className={secondary} onClick={() => setExpanded((v) => !v)} aria-expanded={expanded}>
            {t("cookies.setPreferences")}
          </button>
          <button type="button" className={secondary} onClick={deny}>
            {t("cookies.deny")}
          </button>
          {expanded && (
            <button type="button" className={secondary} onClick={allowSelection}>
              {t("cookies.allowSelection")}
            </button>
          )}
          <button type="button" className="btn-rose !py-2.5 !px-5" onClick={allowAll}>
            {t("cookies.allowAll")}
          </button>
        </div>
      </div>

      {expanded && (
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.35, ease: EASE }}
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mt-5 pt-5 border-t border-zinc-200"
        >
          <Toggle
            checked
            disabled
            label={t("cookies.categories.necessary.title")}
            desc={t("cookies.categories.necessary.desc")}
            badge={t("cookies.alwaysOn")}
          />
          {OPTIONAL.map((key) => (
            <Toggle
              key={key}
              checked={choices[key]}
              onChange={(v) => setChoices((c) => ({ ...c, [key]: v }))}
              label={t(`cookies.categories.${key}.title`)}
              desc={t(`cookies.categories.${key}.desc`)}
            />
          ))}
        </motion.div>
      )}
    </motion.div>
  );
};

export default CookieConsent;
